import {Fragment, PropsWithChildren, useEffect, useId, useRef} from 'react';
import {Dialog, Transition} from '@headlessui/react';

export default function Modal({
    children,
    show = false,
    maxWidth = '2xl',
    closeable = true,
    onClose = () => {},
    className = '',
    blurBackground = false,
}: PropsWithChildren<{
    show: boolean;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'none';
    closeable?: boolean;
    onClose: CallableFunction;
    className?: string;
    blurBackground?: boolean;
}>) {

    const modalId = useId();
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!show) return;

        const vv = window.visualViewport;
        if (!vv) return;

        const onResize = () => {
            if (scrollRef.current) {
                scrollRef.current.style.height = `${vv.height}px`;
            }
        };

        onResize();
        vv.addEventListener('resize', onResize);
        return () => vv.removeEventListener('resize', onResize);
    }, [show]);

    const close = () => {
        if (closeable) {
            onClose();
        }
    };

    useEffect(() => {
        if (show) {
            const handleEscape = (e: KeyboardEvent) => {
                if (e.key === 'Escape' && closeable) {
                    const modals = document.querySelectorAll('[data-modal-stack]');
                    const currentModal = document.querySelector(`[data-modal-id="${modalId}"]`);
                    if (modals.length > 0 && modals[modals.length - 1] === currentModal) {
                        close();
                    }
                }
            };

            document.addEventListener('keydown', handleEscape);
            return () => {
                document.removeEventListener('keydown', handleEscape);
            };
        }
    }, [show, closeable, modalId]);

    const maxWidthClass = {
        xs: 'sm:max-w-xs',
        sm: 'sm:max-w-sm',
        md: 'sm:max-w-md',
        lg: 'sm:max-w-lg',
        xl: 'sm:max-w-2xl',
        'none': 'container',
        '2xl': 'sm:max-w-3xl',
    }[maxWidth];

    return (
        <Transition show={show} as={Fragment} leave="duration-200">
            <Dialog
                as="div"
                data-modal-stack
                data-modal-id={modalId}
                className={`fixed inset-0 overflow-y-auto px-4 py-6 sm:px-0 z-50 transform transition-all ${className || ''}`}
                onClose={close}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className={`fixed inset-0 bg-gray-500/75 ${blurBackground ? 'backdrop-blur-sm' : ''}`}/>
                </Transition.Child>
                <div ref={scrollRef} className="fixed inset-x-0 top-0 h-[100dvh] overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel
                                className={`my-6 bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-xl transform transition-all w-full mx-auto ${maxWidthClass}`}
                            >
                                {children}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
